import React, {useEffect, useState} from 'react';
import useSubmitResponse from './useSubmitResponse';
import useFetchUser from "../../hooks/useFetchUser";
import useFetchModule from "../../hooks/useFetchModule";
import format from 'date-fns/format';
import isFuture from 'date-fns/isFuture';
import isPast from 'date-fns/isPast';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import parseISO from 'date-fns/parseISO';
import isWithinInterval from 'date-fns/isWithinInterval';
import {useFormatValidationErrors} from "../../hooks/response";
import FormControl from "../../components/FormControl";
import { useTranslation } from "react-i18next";

const PollModule = ({className = '', module: pageModule}) => {
  const {isLoading: isUserLoading, isError: isUserError, data: user} = useFetchUser();
  const {isLoading: isModuleLoading, isError: isModuleError, data: module} = useFetchModule(pageModule.id);
  const {query, formik, mutation} = useSubmitResponse(pageModule, user);

  const [isPollOpen, setPollOpen] = useState(false);

  const startsAt = module?.starts_at ? parseISO(module.starts_at) : null;
  const endsAt = module?.ends_at ? parseISO(module.ends_at) : null;
  const {t} = useTranslation('lounge')

  useEffect(() => {
    if(!module?.id) {
      return;
    }

    const updatePollStatus = () => {
      const currDate = new Date();
      if(!startsAt && !endsAt) {
        setPollOpen(true);
      }
      else if(startsAt && !endsAt && isPast(startsAt)) {
        setPollOpen(true);
      }
      else if(!startsAt && endsAt && isFuture(endsAt)) {
        setPollOpen(true);
      }
      else if(startsAt && endsAt && isWithinInterval(currDate, {start: startsAt, end: endsAt})) {
        setPollOpen(true);
      }
      else {
        setPollOpen(false);
      }
    };

    updatePollStatus();

    const currDate = new Date();

    let startTimer = undefined;
    if(startsAt && isFuture(startsAt)) {
      startTimer = setTimeout(updatePollStatus, differenceInMilliseconds(startsAt, currDate));
    }

    let endTimer = undefined;
    if(endsAt && isFuture(endsAt)) {
      endTimer = setTimeout(updatePollStatus, differenceInMilliseconds(endsAt, currDate));
    }

    return () => {
      if(startTimer) clearTimeout(startTimer);
      if(endTimer) clearTimeout(endTimer);
    }
  }, [module?.id]);

  const maxEntries = module?.config?.entries_per_user || null;
  const commonClassName = "text-white text-opacity-90 font-semibold p-4 block text-center rounded-lg mb-5"

  if(isUserLoading || isModuleLoading || query.isLoading) {
    return <div className={"module "+className}><div className="block text-center">{t('loading')}</div></div>;
  }
  else if(isUserError || isModuleError || query.isError) {
    return <div className={"module "+className}><div className={`bg-green-600 ${commonClassName}`}>{t('error')}</div></div>;
  }
  else if(mutation.isSuccess || (maxEntries && query.data?.length >= maxEntries)) {
    return <div className={"module "+className}><div className={`bg-green-500 ${commonClassName}`}>{t('thank-you')}</div></div>;
  }
  else if(!isPollOpen && isPast(endsAt)) {
    return <div className={"module "+className}><div className={`bg-blue-500 ${commonClassName}`}>{t('poll-closed')}</div></div>;
  }
  else if(!isPollOpen && startsAt && endsAt) {
    return <div className={"module "+className}><div className={`bg-blue-500 ${commonClassName}`}>{t('poll-open-from')}{format(startsAt, "h:mmaaa")}{t('to')}{format(endsAt, "h:mmaaa")}{t('poll-open-from-2')}{t('check-later')}</div></div>;
  }
  else if(!isPollOpen && startsAt) {
    return <div className={"module "+className}><div className={`bg-blue-500 ${commonClassName}`}>{t('poll-open-at')}{format(startsAt, "h:mmaaa")}{t('poll-open-at-2')}{t('check-later')}.</div></div>;
  }
  else if(!isPollOpen && endsAt) {
    return <div className={"module "+className}><div className={`bg-blue-500 ${commonClassName}`}>{t('poll-open-until')}{format(endsAt, "h:mmaaa")}{t('poll-open-until-2')}{t('can-cast-vote-now')}</div></div>;
  }
  else if(!isPollOpen) {
    return <div className={"module "+className}><div className={`bg-blue-500 ${commonClassName}`}>{t('poll-not-open')}{t('check-later')}</div></div>;
  }

  const questions = Object.values(module?.config?.questions || {});
  const errors = useFormatValidationErrors(mutation.error || {}, formik.errors);

  console.log(formik.values);
  return (
    <div className={"module "+className}>
      <form onSubmit={formik.handleSubmit} disabled={formik.isSubmitting}>
        {module?.config?.description && <div className="mb-3 lounge-body" dangerouslySetInnerHTML={{__html: module?.config?.description}} />}
        {mutation.isError && (
          <div className="bg-red-600 text-white text-opacity-80 font-semibold p-4 block rounded text-center rounded-lg mb-5">
            {t('submission-failed')}
            {mutation.error?.response?.data?.message && (<span className="block">{mutation.error?.response?.data?.message}</span>)}
          </div>
        )}

        {questions.map(item => {
          if(['checkbox', 'radio', 'select', 'likert'].includes(item.type)) {
            return (
              <div key={item.id} className="mb-8">
                <h4 className={"text-white text-lg font-bold mb-2"}>{item.label}</h4>
                {item.choices.map(choice => (
                  <label className="flex items-center space-x-4 mb-2" htmlFor={item.id+choice.id}>
                    <input
                      type={item?.multiple || false ? 'checkbox' : 'radio'}
                      key={item.id+choice.id}
                      id={item.id+choice.id}
                      name={item.id}
                      value={choice.id}
                      checked={item?.multiple || false ? (formik.values[item.id] || []).includes(choice?.id?.toString()) : formik.values[item.id] === choice.id}
                      onChange={formik.handleChange(item.id)}
                      disabled={formik.isSubmitting}
                      className={`${item?.type == 'checkbox' && 'form-tick checkbox'} ${item?.type == 'radio' && 'form-radio checkbox'}`}
                    />
                    <span className="font-semibold text-base flex-1 text-white mb-1 leading-snug">{choice.label}</span>
                  </label>
                ))}
                {(errors[item.id] || null) && <p data-error={true} className={'text-red-400 font-bold text-sm'}>{errors[item.id] || null}</p>}
              </div>
            );
          }
        })}

        <button type="submit" className="btn btn-block btn-yellow" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
          {t('submit')}
        </button>
      </form>
    </div>
  );
};

export default PollModule;
